import { useEffect, useRef, useState } from "react";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";

export type SlackPermissions = {
  notifications: boolean;
  bot: boolean;
  ingest: boolean;
};

export default function SlackPermissionsDialog({
  open,
  onClose,
  onContinue,
  initialPermissions,
}: {
  open: boolean;
  onClose: () => void;
  onContinue: (permissions: SlackPermissions) => void;
  initialPermissions: SlackPermissions;
}) {
  const [permissions, setPermissions] =
    useState<SlackPermissions>(initialPermissions);

  // Reset to initial values only when the dialog is opened, so unstable
  // parent references don't clobber the user's in-progress selections.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setPermissions(initialPermissions);
    }
    prevOpenRef.current = open;
  }, [open, initialPermissions]);

  const canContinue =
    permissions.notifications || permissions.bot || permissions.ingest;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-label="Slack permissions dialog"
      dialogClassName="md:max-w-[708px]"
    >
      <div className="p-8 bg-white rounded-[12px]">
        <div className="space-y-6">
          <div className="space-y-3">
            <ModalTitle onClose={onClose} className="text-gray-900">
              Choose what Topicflow can do in Slack
            </ModalTitle>
            <p className="text-sm text-gray-800">
              Set the permissions you want Topicflow to use. You can change
              these anytime. After this step, you’ll be taken to Slack to
              confirm access.
            </p>
          </div>

          <div className="space-y-4">
            <PermissionRow
              checked={permissions.notifications}
              onChange={(checked) =>
                setPermissions((current) => ({
                  ...current,
                  notifications: checked,
                }))
              }
              title="Send notifications"
              description="Topicflow can send reminders and updates to your team in Slack."
            />

            <PermissionRow
              checked={permissions.bot}
              onChange={(checked) =>
                setPermissions((current) => ({ ...current, bot: checked }))
              }
              title="Enable the Topicflow bot"
              description="Use Topicflow in Slack to ask questions, get insights, and pull up work history."
            />

            <PermissionRow
              checked={permissions.ingest}
              onChange={(checked) =>
                setPermissions((current) => ({ ...current, ingest: checked }))
              }
              title="Ingest messages for AI"
              description="Topicflow can read public Slack messages to power review context, updates, and coaching suggestions."
            />
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3 justify-end">
          <Button
            theme={buttonTheme.lightGray}
            size={ButtonSize.large}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            theme={buttonTheme.primary}
            size={ButtonSize.large}
            disabled={!canContinue}
            onClick={() => onContinue(permissions)}
          >
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PermissionRow({
  checked,
  onChange,
  title,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: string;
  description: string;
}) {
  return (
    <label className="flex gap-3">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 rounded border-2 border-blue-600 accent-blue-600 focus:ring-2 focus:ring-blue-500"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <div className="space-y-1">
        <p className="text-sm font-semibold text-gray-600">{title}</p>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </label>
  );
}
